import { promiseTimeout, unrefElement } from '@vueuse/core';
import {
  isWalkthroughTargetVisible,
  useWalkthroughController,
} from '@/composables/useWalkthroughController';
import { getWalkthroughTarget } from '@/composables/useWalkthroughTarget';
import { runWalkthroughSetup } from '@/composables/walkthroughSetups';

const FIRST_VISIBLE_TIMEOUT_MS = 4000;

/**
 * Once something is showable, how much longer to wait for the rest. Short: enough for a CSS
 * transition or a paint or two, while a walkthrough containing a permanently hidden step
 * shouldn't stall on it.
 */
const ALL_VISIBLE_TIMEOUT_MS = 1000;
const VISIBILITY_POLL_MS = 50;

/** Cap on waiting for the destination page, so a missed hook can't stall a launch. */
const PAGE_READY_TIMEOUT_MS = 2000;

/**
 * Resolves once the page navigated to has actually mounted.
 *
 * `await navigateTo(...)` only tells you the route was confirmed, not that the destination page has
 * finished setting itself up — and anything a `setup` does in that gap is unsafe. The API client
 * aborts every in-flight request from `router.beforeEach` (see plugins/api.js), so a fetch fired
 * too early is cancelled by the tail of the very navigation that led to it, then swallowed: the
 * saved-search store treats an AbortError as expected and simply leaves its list empty. The
 * symptom is a panel that works when launched from its own page and shows an empty state when
 * launched from anywhere else.
 *
 * `page:finish` is the signal Nuxt gives for that, with a timeout because it fires per real page
 * transition and we would rather start slightly early than not at all.
 */
const whenPageReady = async () => {
  // Outside a Nuxt runtime (unit tests) there is no page transition to wait for.
  let nuxtApp = null;
  try {
    nuxtApp = useNuxtApp();
  } catch {
    nuxtApp = null;
  }

  if (!nuxtApp?.hook) {
    await nextTick();
    return;
  }

  await new Promise((resolve) => {
    const stopWaiting = () => {
      clearTimeout(timer);
      unhook();
      resolve();
    };
    const unhook = nuxtApp.hook('page:finish', stopWaiting);
    const timer = setTimeout(stopWaiting, PAGE_READY_TIMEOUT_MS);
  });

  await nextTick();
};

const pollUntil = async (predicate, timeoutMs) => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    if (predicate()) {
      return true;
    }
    await promiseTimeout(VISIBILITY_POLL_MS);
  }

  return false;
};

/**
 * Resolves once the browser reports the current scroll has actually finished — including an
 * animated `scroll-behavior: smooth` scroll — rather than guessing how long that takes. The
 * timeout is only a safety net for browsers without `scrollend` (Safari < 17.4) and for the
 * case where the target was already in view, so scrolling never started and `scrollend` never
 * fires.
 */
const SCROLL_SETTLE_TIMEOUT_MS = 1000;

const waitForScrollEnd = () =>
  new Promise((resolve) => {
    const finish = () => {
      document.removeEventListener('scrollend', finish);
      clearTimeout(fallback);
      resolve();
    };
    const fallback = setTimeout(finish, SCROLL_SETTLE_TIMEOUT_MS);
    document.addEventListener('scrollend', finish, { once: true });
  });

/**
 * A step marked `scrollIntoView` may start off-screen, and `isWalkthroughTargetVisible` never
 * scrolls to fix that — so scroll to the target ourselves, once it registers.
 *
 * Never scroll again until the previous attempt has fully settled — restarting a scroll mid-
 * animation just keeps nudging it sideways and it never finishes. Layout can keep shifting
 * after that (async content loading in above the target), so keep re-scrolling, one settled
 * attempt at a time, until the target is actually visible or the deadline runs out.
 */
const scrollStepIntoViewUntilVisible = async (step) => {
  const deadline = Date.now() + FIRST_VISIBLE_TIMEOUT_MS;
  const resolveElement = () => unrefElement(getWalkthroughTarget(step.stepId));

  await pollUntil(() => Boolean(resolveElement()), FIRST_VISIBLE_TIMEOUT_MS);

  while (Date.now() < deadline) {
    if (isWalkthroughTargetVisible(step.stepId)) {
      return;
    }

    const element = resolveElement();
    if (!element?.scrollIntoView) {
      return;
    }

    element.scrollIntoView({ block: 'center' });
    await waitForScrollEnd();
  }
};

const scrollFlaggedStepsIntoView = (steps) =>
  Promise.all(steps.filter((step) => step.scrollIntoView).map(scrollStepIntoViewUntilVisible));

const waitForStepsToSettle = async (steps) => {
  await scrollFlaggedStepsIntoView(steps);

  const isVisible = (step) => isWalkthroughTargetVisible(step.stepId);
  await pollUntil(() => steps.some(isVisible), FIRST_VISIBLE_TIMEOUT_MS);
  await pollUntil(() => steps.every(isVisible), ALL_VISIBLE_TIMEOUT_MS);
};

/**
 * Starts a Guided Walkthrough for an announcement, from wherever the carrier happens to
 * be. Shared by every entry point (the What's New panel's card CTA, and the login modal's
 * once it's wired) so the sequence can't drift between them:
 *
 *   navigate to the announcement's `route`
 *     → run its `setup`, putting the UI into the state the steps need
 *       → wait for the page to settle: every step's target actually visible
 *         → start the Controller
 *
 * Order matters at every arrow. Navigation comes first because a walkthrough only shows
 * steps that are visible when it starts. The setup runs after arriving, since it usually
 * manipulates state belonging to the destination page.
 *
 * The wait is on *visibility*, not registration, and that distinction is load-bearing. A
 * target is routinely registered long before it can be shown: the saved-search sidebar is
 * mounted for the whole session and merely slides in and out, so its contents are in the
 * registry from page load while parked off-screen. Waiting for registration returned
 * immediately, `start()` found nothing visible, and the walkthrough ended on the tick it
 * began.
 */
/**
 * Says out loud, in development, which steps are about to be skipped and why.
 *
 * Every authoring mistake in this feature fails the same silent way: the walkthrough runs, drops
 * the step, and reports nothing — a step id no component registers, a `ref` bound with `:ref`
 * so it never populates, a control that has a separate mobile implementation nobody registered,
 * a target still hidden behind a panel because the announcement declares no `setup`. Each of
 * those cost real debugging time to find, and each is obvious the moment it's named.
 *
 * Dev-only: in production a skipped step is deliberate, graceful behavior, not something to
 * narrate to a carrier's console.
 */
const warnAboutSkippedSteps = (announcement, steps) => {
  if (!useIsDevelopment()) {
    return;
  }

  const skipped = steps.filter((step) => !isWalkthroughTargetVisible(step.stepId));
  if (!skipped.length) {
    return;
  }

  const details = skipped.map((step) => {
    const registered = Boolean(unrefElement(getWalkthroughTarget(step.stepId)));
    return registered
      ? `  • ${step.stepId} — registered, but not visible. Is it behind a panel this walkthrough does not open with a "setup"?`
      : `  • ${step.stepId} — no component registered this id. Check useWalkthroughTarget + a static ref="…" binding (never :ref), in every component that renders this control, mobile and desktop.`;
  });

  // eslint-disable-next-line no-console -- authoring aid; dev-only, see above
  console.warn(
    [
      `[walkthrough] "${announcement.id}" is starting with ${skipped.length} of ${steps.length} step(s) that cannot be shown:`,
      ...details,
    ].join('\n'),
  );
};

/**
 * The path we're on, read from the browser rather than `useRoute()`: this composable is called
 * from a component's setup but `launchWalkthrough` runs long after, and the app is SPA-only, so
 * history keeps `location.pathname` current.
 */
const currentPath = () => (typeof window === 'undefined' ? '' : window.location.pathname);

/**
 * Set right before navigating to a walkthrough's `route`; consumed by that destination page's
 * own `scrollToTop` page meta (see `pages/carrier-profile/your-profile.vue`). Nuxt's default
 * scroll-to-top on navigation can land *after* this composable's own scroll-into-view work and
 * undo it, so a page with a `scrollIntoView` step opts out of the reset via this flag instead
 * of racing it.
 */
let skipNextScrollToTopReset = false;

export const consumeSkipScrollToTopReset = () => {
  const shouldSkip = skipNextScrollToTopReset;
  skipNextScrollToTopReset = false;
  return shouldSkip;
};

export const useWalkthroughLaunch = () => {
  const walkthroughController = useWalkthroughController();
  const { trackEvent, eventNames } = useAcvAnalytics();

  const launchWalkthrough = async (announcement, { source } = {}) => {
    const steps = announcement?.steps ?? [];
    if (!steps.length) {
      return;
    }

    // Only wait when a real page transition happens — launching from the announcement's own
    // route navigates nowhere, so `page:finish` would never fire and the wait would just
    // burn its timeout before the panel opened.
    if (announcement.route && currentPath() !== announcement.route) {
      if (steps.some((step) => step.scrollIntoView)) {
        skipNextScrollToTopReset = true;
      }
      await navigateTo(announcement.route);
      await whenPageReady();
    }

    runWalkthroughSetup(announcement.setup);

    await waitForStepsToSettle(steps);

    warnAboutSkippedSteps(announcement, steps);

    trackEvent(eventNames.FEATURE_ANNOUNCEMENT_WALKTHROUGH_STARTED, {
      announcement_id: announcement.id,
      announcement_name: announcement.name,
      source,
    });

    walkthroughController.start(announcement.steps, {
      onComplete: ({ finalStepIndex, totalSteps }) => {
        trackEvent(eventNames.FEATURE_ANNOUNCEMENT_WALKTHROUGH_COMPLETED, {
          announcement_id: announcement.id,
          announcement_name: announcement.name,
          final_step_index: finalStepIndex,
          total_steps: totalSteps,
        });
      },
      onCancel: ({ finalStepIndex, totalSteps }) => {
        trackEvent(eventNames.FEATURE_ANNOUNCEMENT_WALKTHROUGH_CANCELLED, {
          announcement_id: announcement.id,
          announcement_name: announcement.name,
          final_step_index: finalStepIndex,
          total_steps: totalSteps,
        });
      },
    });
  };

  return { launchWalkthrough };
};
